export const STATUS = {
  scheduled: { label: "Scheduled", cls: "bg-[#E6EAEF] text-[#12233D] border-[#2B4C7E]/30" },
  in_progress: { label: "In Progress", cls: "bg-[#F5ECE3] text-[#8A5028] border-[#B87333]/40" },
  completed: { label: "Completed", cls: "bg-[#E6EFEA] text-[#1A4331] border-[#2D6A4F]/35" },
  cancelled: { label: "Cancelled", cls: "bg-[#F0E6E6] text-[#7A0000] border-[#4A0000]/30" },
};

export const STATUS_DOT = {
  scheduled: "#2B4C7E",
  in_progress: "#B87333",
  completed: "#1A4331",
  cancelled: "#7A0000",
};

export const ONBOARDING_BASE = "https://www.hub.goreelty.com";
export function onboardingLink(role, slug) {
  if (!slug) return "";
  // Everyone shares the one main "book a shoot" page; the slug rides along for attribution.
  return `${ONBOARDING_BASE}/book?ref=${slug}`;
}

export const ROLE_LABEL = { admin: "Admin", employee: "Employee", agent: "Agent", photographer: "Photographer", pilot: "Pilot", dual: "Dual", system: "Weather Alert", guest: "LeadLander Lead" };

export function weatherInfo(code) {
  if (code === 0) return { label: "Clear", icon: "sun" };
  if ([1, 2, 3].includes(code)) return { label: "Partly Cloudy", icon: "cloudsun" };
  if ([45, 48].includes(code)) return { label: "Fog", icon: "fog" };
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { label: "Rain", icon: "rain" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { label: "Snow", icon: "snow" };
  if ([95, 96, 99].includes(code)) return { label: "Storm", icon: "storm" };
  return { label: "—", icon: "cloud" };
}

export const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const REELTY_PRICING = { photoOnly: 279, allInclusive: 495, noInterior: 135, oversized: 197, oversizedSqft: 5000, oversizedAcre: 5000 };
// The $495 all-inclusive package is presented as the "Penthouse Package" (All-Inclusive follows it).
export const PACKAGE_LABEL = { "All-Inclusive": "Penthouse Package", "Photo Only": "Photo Only" };
export const pkgLabel = (t) => PACKAGE_LABEL[t] || t;
export function sizeSurcharge(sqft, acres) {
  const s = Number(sqft) || 0, a = Number(acres) || 0;
  if (s > REELTY_PRICING.oversizedSqft || a > REELTY_PRICING.oversizedAcre) return REELTY_PRICING.oversized;
  return 0;
}
export function reeltyPackagePrice(pkg, interior, sqft, acres) {
  let base;
  if (pkg === "Photo Only") base = REELTY_PRICING.photoOnly;
  else if (pkg === "All-Inclusive") base = REELTY_PRICING.allInclusive - (interior ? 0 : REELTY_PRICING.noInterior);
  else return 0;
  return base + sizeSurcharge(sqft, acres);
}

// Curated stock property photos by package type — shown full-scale (with an oxblood overlay)
// for any shoot that has no real uploaded photo yet.
const FALLBACK_PHOTOS = {
  Residential: [
    "https://images.unsplash.com/photo-1628744448839-a475cc0e90c3?w=800&q=80",
    "https://images.unsplash.com/photo-1766603636700-e9d80473f40f?w=800&q=80",
    "https://images.unsplash.com/photo-1628744448840-55bdb2497bd4?w=800&q=80",
  ],
  Commercial: [
    "https://images.unsplash.com/photo-1774845334998-616e157ec181?w=800&q=80",
    "https://images.unsplash.com/photo-1759329172064-5be704e6cca5?w=800&q=80",
  ],
  Land: [
    "https://images.unsplash.com/photo-1594220862488-117b78382514?w=800&q=80",
    "https://images.unsplash.com/photo-1595598239736-223ad4fe7da5?w=800&q=80",
  ],
};
const DEFAULT_PHOTO = FALLBACK_PHOTOS.Residential[0];
// Ultimate inline fallback (no network) if a hosted image ever fails to load.
export const PLACEHOLDER_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='533' viewBox='0 0 800 533'%3E%3Crect width='800' height='533' fill='%23FDFBF7'/%3E%3Cg fill='none' stroke='%234A0000' stroke-width='6' stroke-linejoin='round' stroke-linecap='round'%3E%3Cpath d='M300 305 L400 225 L500 305 L500 385 L300 385 Z'/%3E%3Cpath d='M345 385 L345 325 L385 325 L385 385'/%3E%3Ccircle cx='448' cy='345' r='16' stroke='%23B08D57'/%3E%3C/g%3E%3C/svg%3E";
// Swap a broken property <img> to the guaranteed inline placeholder.
export function onPropImgError(e) { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_SVG; }

// True when the card is showing a curated/generic stock photo (no real uploaded photo yet).
export function isGenericCover(p) {
  return !p?.image;
}

// Best cover photo for a shoot: real property photo if present, else a stable curated stock photo.
export function coverImage(p) {
  if (p?.image) return p.image;
  const list = FALLBACK_PHOTOS[p?.package_type] || [DEFAULT_PHOTO];
  const key = `${p?.id || p?.property_id || p?.title || ""}`;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return list[h % list.length];
}

// ---------- Delivery deadline (12h raws-only / 24h editing) from shoot start ----------
export function shootStart(p) {
  if (!p?.shoot_date) return null;
  const d = new Date(p.shoot_date);
  return isNaN(d.getTime()) ? null : d;
}
export function deliveryWindowHours(p) {
  return p?.edited ? 24 : 12;
}
export function deliveryDeadline(p) {
  const s = shootStart(p);
  if (!s) return null;
  return new Date(s.getTime() + deliveryWindowHours(p) * 3600 * 1000);
}

// ---------- Photographer deliverable grades (admin A–F +/-) ----------
export const GRADE_OPTIONS = ["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D+", "D", "D-", "F"];
export function gradeColor(g) {
  if (!g) return "bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6]";
  const l = g[0];
  if (l === "A") return "bg-green-100 text-green-800 border-green-200";
  if (l === "B") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (l === "C") return "bg-amber-50 text-amber-800 border-amber-200";
  if (l === "D") return "bg-orange-50 text-orange-800 border-orange-200";
  return "bg-red-50 text-red-800 border-red-200"; // F
}

// ---------- Shoot time slots + photographer availability ----------
function _fmt12(mins) {
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}

// 2-hour shoot slots (:00/:15/:30/:45), 7 AM–7 PM ET (latest start 5 PM ends 7 PM).
export const TIME_SLOTS = [];
for (let mins = 7 * 60; mins <= 17 * 60; mins += 15) {
  const h = Math.floor(mins / 60), m = mins % 60;
  TIME_SLOTS.push({ value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`, label: `${_fmt12(mins)} – ${_fmt12(mins + 120)}` });
}

// Half-hourly options (7 AM–7 PM) for the availability editor's From/To selects.
export const HOUR_OPTIONS = [];
for (let mins = 7 * 60; mins <= 19 * 60; mins += 30) {
  const h = Math.floor(mins / 60), m = mins % 60;
  HOUR_OPTIONS.push({ value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`, label: _fmt12(mins) });
}

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Default weekly schedule: every day 7 AM–7 PM.
export const DEFAULT_AVAILABILITY = Object.fromEntries(
  [0, 1, 2, 3, 4, 5, 6].map((d) => [String(d), { enabled: true, start: "07:00", end: "19:00" }])
);

// Is the photographer free on weekday (0=Sun..6=Sat) at slot start "HH:MM"? No schedule = available.
export function slotAvailable(availability, weekday, hhmm) {
  if (!availability) return true;
  const day = availability[String(weekday)];
  if (!day) return true;
  if (!day.enabled) return false;
  return (day.start || "07:00") <= hhmm && hhmm < (day.end || "17:00");
}

export const fmtDate = (iso) => {
  if (!iso) return "No date set";
  try {
    const s = String(iso);
    // Date-only strings ("YYYY-MM-DD") parse as UTC midnight → off-by-one in US zones. Treat as local.
    const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

export const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

// Just the 2-hour time window, e.g. "9:00 AM–11:00 AM" (empty if date-only).
export const fmtShootTime = (iso) => {
  const start = fmtTime(iso);
  if (!start) return "";
  try {
    const end = fmtTime(new Date(new Date(iso).getTime() + 2 * 3600 * 1000).toISOString());
    return `${start}–${end}`;
  } catch { return start; }
};

// Date + 2-hour Eastern shoot window, e.g. "Jun 20, 2026 · 9:00 AM–11:00 AM".
export const fmtShootWhen = (iso) => {
  if (!iso) return "No date set";
  const start = fmtTime(iso);
  if (!start) return fmtDate(iso);
  try {
    const end = fmtTime(new Date(new Date(iso).getTime() + 2 * 3600 * 1000).toISOString());
    return `${fmtDate(iso)} · ${start}–${end}`;
  } catch { return `${fmtDate(iso)} · ${start}`; }
};


// ---------- Due-date view/filter (receivables & payables) ----------
export const DUE_FILTERS = [
  { value: "all", label: "All due dates" },
  { value: "overdue", label: "Overdue" },
  { value: "week", label: "Due this week" },
  { value: "month", label: "Due this month" },
  { value: "upcoming", label: "Not yet due" },
  { value: "none", label: "No due date" },
];

// Effective due date (ISO string) — falls back to shoot date, then null.
export function dueOf(row) {
  return row?.due || row?.due_date || row?.shoot_date || null;
}

const _toDate = (iso) => {
  const s = String(iso);
  const d = /^\d{4}-\d{2}-\d{2}/.test(s) ? new Date(`${s.slice(0, 10)}T00:00:00`) : new Date(s);
  return isNaN(d) ? null : d;
};

// Days past due (>0 overdue, <=0 upcoming); null when no valid due date.
export function daysOverdue(row) {
  const d = _toDate(dueOf(row));
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((today - d) / 86400000);
}

export function matchesDueFilter(row, mode) {
  if (mode === "all") return true;
  const due = dueOf(row);
  if (mode === "none") return !due;
  if (!due) return false;
  const d = _toDate(due);
  if (!d) return false;
  const settled = !!(row.paid || row.client_paid || row.refunded);
  const od = daysOverdue(row);
  if (mode === "overdue") return !settled && od > 0;
  if (mode === "upcoming") return od <= 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (mode === "week") {
    const end = new Date(today); end.setDate(end.getDate() + 7);
    return d >= today && d <= end;
  }
  if (mode === "month") {
    return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth();
  }
  return true;
}

// Filter + optional sort ("none"|"asc"|"desc") a list of billing/pay rows by due date.
export function applyDueView(rows, mode, sort) {
  let out = (rows || []).filter((r) => matchesDueFilter(r, mode));
  if (sort === "asc" || sort === "desc") {
    out = [...out].sort((a, b) => {
      const da = dueOf(a), db_ = dueOf(b);
      if (!da && !db_) return 0;
      if (!da) return 1;
      if (!db_) return -1;
      return sort === "asc" ? (da < db_ ? -1 : da > db_ ? 1 : 0) : (da < db_ ? 1 : da > db_ ? -1 : 0);
    });
  }
  return out;
}
